import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { api, DfeLegacyImportResult, DfePositionConflict } from '@/lib/api';

interface ImportResultDisplayProps {
  result: DfeLegacyImportResult;
  onReset: () => void;
  onRetry?: () => void;
}

export function ImportResultDisplay({ result, onReset, onRetry }: ImportResultDisplayProps) {
  const isSuccess = result.importStatus === 'completed';
  const hasSkipped = result.skippedItems.length > 0;
  const [positionConflicts, setPositionConflicts] = useState<DfePositionConflict[]>([]);

  // Check for position conflicts after a successful import
  useEffect(() => {
    if (!isSuccess || result.itemsImported === 0) return;
    api
      .getDfePositionConflicts()
      .then((data) => setPositionConflicts(data.conflicts))
      .catch(() => {
        /* ignore — non-critical */
      });
  }, [isSuccess, result.itemsImported]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Import {isSuccess ? 'Complete' : 'Failed'}
          <Badge variant={isSuccess ? 'default' : 'destructive'}>{result.importStatus}</Badge>
        </CardTitle>
        <CardDescription>
          Legacy account: {result.legacyUsername} ({result.legacyServer})
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSuccess && (
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{result.itemsImported}</div>
              <div className="text-sm text-muted-foreground">Items Imported</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{result.itemsSkipped}</div>
              <div className="text-sm text-muted-foreground">Items Skipped</div>
            </div>
          </div>
        )}

        {result.errorMessage && (
          <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            {result.errorMessage}
          </div>
        )}

        {hasSkipped && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Skipped items (no matching definition):</div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {result.skippedItems.map((name, i) => (
                <div key={i} className="text-sm text-muted-foreground px-2 py-1 bg-muted rounded">
                  {name}
                </div>
              ))}
            </div>
          </div>
        )}

        {positionConflicts.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm">
            <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
            <div>
              <span className="font-medium">Position conflicts detected: </span>
              {positionConflicts.map((c, i) => (
                <span key={`${c.category}:${c.position}`}>
                  {i > 0 && ', '}
                  {c.category}/{c.position}
                </span>
              ))}
              {'. '}Check your inventory for details.
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {isSuccess && (
            <Button asChild variant="default">
              <Link to="/a/arche_dfe">View Inventory</Link>
            </Button>
          )}
          {isSuccess && hasSkipped && onRetry && (
            <Button variant="secondary" onClick={onRetry}>
              Retry Skipped Items
            </Button>
          )}
          <Button variant="outline" onClick={onReset}>
            {isSuccess ? 'Import Another' : 'Try Again'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
